import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Box, Button, Chip, CircularProgress, useMediaQuery } from "@mui/material";
import PublishOutlinedIcon from "@mui/icons-material/PublishOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import PlayCircleOutlinedIcon from "@mui/icons-material/PlayCircleOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useNavigate } from "react-router-dom";
import { dt, accentGradient, accentGradientHover } from "../../../../shared/themes/redesignTokens";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useDataRefresh from "../../../../shared/notifications/hooks/useDataRefresh";
import useVisibilityRefresh from "../../../../shared/hooks/useVisibilityRefresh";
import { ServiceRequestStatusCode, AcceptedOfferSummary } from "../../models/models";
import { ServiceExecutionState, UpdateType } from "../../models/executionModels";
import { ServiceReviewsState } from "../../models/reviewModels";
import { OfferStatusCode } from "../../../serviceOffers/models/models";
import {
  fetchCompletion,
  fetchProgressUpdates,
  clearCompletion,
  clearProgressUpdates,
  confirmCompletion,
  requestRevision,
} from "../../serviceExecutionSlice";
import {
  checkHasReviewed,
  createReview,
  fetchReviewsForServiceRequest,
  clearReviews,
} from "../../serviceReviewsSlice";
import { getServiceRequest } from "../../customerServicingSlice";
import receivedOffersApi from "../../api/receivedOffersApi";
import {
  buildLogbookEntries,
  LogbookEntry,
  LogbookMilestoneEntry,
  LogbookProgressNote,
  milestoneColorMap,
  MilestoneType,
} from "./logbookUtils";
import LogbookMilestone from "./LogbookMilestone";
import RfsCompletionCard from "./RfsCompletionCard";
import RfsReviewSection from "./RfsReviewSection";
import CompareOffersDialog from "../CompareOffersDialog";
import ConfirmCompletionDialog from "../ConfirmCompletionDialog";
import RequestRevisionDialog from "../RequestRevisionDialog";
import LeaveReviewDialog from "../LeaveReviewDialog";
import dayjs from "dayjs";
import { formatDateTimeAt } from "../../../../shared/utils/formatDateTime";

// ===================================================================
//  Props
// ===================================================================
interface RfsUnifiedLogbookProps {
  serviceRequestId: string;
  statusCode: ServiceRequestStatusCode;
  createdDate?: string;
  offersCount: number;
  newOffersCount: number;
  acceptedOffer?: AcceptedOfferSummary | null;
  serviceRequestIssue: string;
  onOfferAction?: () => void;
  onHasReviewedChange?: (val: boolean) => void;
}

// ===================================================================
//  Icon map per milestone type
// ===================================================================
const milestoneIcons: Record<MilestoneType, React.ReactNode> = {
  published: <PublishOutlinedIcon sx={{ fontSize: 18 }} />,
  offerAccepted: <HandshakeOutlinedIcon sx={{ fontSize: 18 }} />,
  serviceConfirmed: <EventAvailableOutlinedIcon sx={{ fontSize: 18 }} />,
  workStarted: <PlayCircleOutlinedIcon sx={{ fontSize: 18 }} />,
  workCompleted: <CheckCircleOutlinedIcon sx={{ fontSize: 18 }} />,
  ownerConfirmed: <VerifiedOutlinedIcon sx={{ fontSize: 18 }} />,
  reviewLeft: <StarOutlinedIcon sx={{ fontSize: 18 }} />,
};

// ===================================================================
//  Statuses that require completion data
// ===================================================================
const COMPLETION_STATUSES = [
  ServiceRequestStatusCode.awaitingCompletionConfirmation,
  ServiceRequestStatusCode.completed,
];

// ===================================================================
//  Component
// ===================================================================
const RfsUnifiedLogbook: React.FC<RfsUnifiedLogbookProps> = ({
  serviceRequestId,
  statusCode,
  createdDate,
  offersCount,
  newOffersCount,
  acceptedOffer,
  serviceRequestIssue,
  onOfferAction,
  onHasReviewedChange,
}) => {
  const { translate } = useTranslation(["servicing", "serviceOffers", "common"]);
  const dispatch = useAppDispatch();
  const isPhone = useMediaQuery("(max-width:768px)");

  // Redux state
  const { completion, progressUpdates } = useAppSelector<ServiceExecutionState>(
    (state: any) => state.serviceExecution
  );
  const { reviewCheck, reviews: srReviews, submitting: reviewSubmitting } =
    useAppSelector<ServiceReviewsState>((state: any) => state.serviceReviews);

  // Dialog states
  const [offersDialogOpen, setOffersDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [completionActionLoading, setCompletionActionLoading] = useState(false);
  const [reviewDismissed, setReviewDismissed] = useState(false);
  const [reviewError, setReviewError] = useState<string | undefined>(undefined);

  // Expand/collapse state
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [allExpanded, setAllExpanded] = useState(false);

  // LOG-5: Jump-to-latest chip visibility
  const [showJumpChip, setShowJumpChip] = useState(false);
  const currentMilestoneRef = useRef<HTMLDivElement>(null);
  const logbookContainerRef = useRef<HTMLDivElement>(null);
  const logbookPdfRef = useRef<HTMLDivElement>(null);
  const [pdfExporting, setPdfExporting] = useState(false);

  const hasReviewed = reviewCheck?.hasReviewed || false;

  // Notify parent of review state for guidance banner
  useEffect(() => {
    onHasReviewedChange?.(hasReviewed);
  }, [hasReviewed, onHasReviewedChange]);

  // ===================================================================
  //  Data fetching
  // ===================================================================
  useEffect(() => {
    if (serviceRequestId) {
      dispatch(fetchProgressUpdates(serviceRequestId));
    }
    return () => {
      dispatch(clearProgressUpdates());
    };
  }, [dispatch, serviceRequestId]);

  useEffect(() => {
    if (COMPLETION_STATUSES.includes(statusCode)) {
      dispatch(fetchCompletion(serviceRequestId));
    }
    return () => {
      dispatch(clearCompletion());
    };
  }, [dispatch, serviceRequestId, statusCode]);

  useEffect(() => {
    if (statusCode === ServiceRequestStatusCode.completed ||
        statusCode === ServiceRequestStatusCode.archived) {
      dispatch(checkHasReviewed(serviceRequestId));
      dispatch(fetchReviewsForServiceRequest(serviceRequestId));
    }
    return () => {
      dispatch(clearReviews());
    };
  }, [dispatch, serviceRequestId, statusCode]);

  // ===================================================================
  //  Real-time refresh — re-fetch sub-data when SignalR events fire
  // ===================================================================
  const refreshSubData = useCallback(() => {
    if (!serviceRequestId) return;
    dispatch(fetchProgressUpdates(serviceRequestId));
    if (COMPLETION_STATUSES.includes(statusCode)) {
      dispatch(fetchCompletion(serviceRequestId));
    }
    if (statusCode === ServiceRequestStatusCode.completed ||
        statusCode === ServiceRequestStatusCode.archived) {
      dispatch(checkHasReviewed(serviceRequestId));
      dispatch(fetchReviewsForServiceRequest(serviceRequestId));
    }
  }, [dispatch, serviceRequestId, statusCode]);

  useDataRefresh("serviceRequest", refreshSubData);
  useDataRefresh("myServiceRequests", refreshSubData);
  useDataRefresh("serviceBoard", refreshSubData);
  useVisibilityRefresh(refreshSubData);

  // ===================================================================
  //  Build logbook entries
  // ===================================================================
  const entries = useMemo(
    () =>
      buildLogbookEntries({
        statusCode,
        createdDate,
        offersCount,
        newOffersCount,
        acceptedOffer,
        completion,
        progressUpdates,
        reviews: srReviews,
        hasReviewed,
      }),
    [statusCode, createdDate, offersCount, newOffersCount, acceptedOffer, completion, progressUpdates, srReviews, hasReviewed]
  );

  // LOG-5: Intersection observer for "Jump to latest" chip on phone
  useEffect(() => {
    if (!isPhone || !currentMilestoneRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setShowJumpChip(!entry.isIntersecting),
      { threshold: 0.3 }
    );
    observer.observe(currentMilestoneRef.current);
    return () => observer.disconnect();
  }, [isPhone, entries]);

  const scrollToLatest = useCallback(() => {
    currentMilestoneRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, []);

  // Set default expanded state: current milestone expanded
  useEffect(() => {
    if (allExpanded) return;
    const currentEntry = entries.find(
      (e) => e.kind === "milestone" && (e as LogbookMilestoneEntry).status === "current"
    );
    if (currentEntry) {
      setExpandedIds(new Set([currentEntry.id]));
    }
  }, [entries, allExpanded]);

  // ===================================================================
  //  Handlers
  // ===================================================================
  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleExpandAll = () => {
    if (allExpanded) {
      // Collapse to just current
      const currentEntry = entries.find(
        (e) => e.kind === "milestone" && (e as LogbookMilestoneEntry).status === "current"
      );
      setExpandedIds(currentEntry ? new Set([currentEntry.id]) : new Set());
      setAllExpanded(false);
    } else {
      setExpandedIds(new Set(entries.map((e) => e.id)));
      setAllExpanded(true);
    }
  };

  const handleConfirmCompletion = async () => {
    setCompletionActionLoading(true);
    try {
      await dispatch(confirmCompletion(serviceRequestId)).unwrap();
      setConfirmDialogOpen(false);
      dispatch(getServiceRequest(serviceRequestId));
    } finally {
      setCompletionActionLoading(false);
    }
  };

  const handleRequestRevision = async (reason: string) => {
    setCompletionActionLoading(true);
    try {
      await dispatch(
        requestRevision({ serviceRequestId, data: { reason } })
      ).unwrap();
      setRevisionDialogOpen(false);
      setConfirmDialogOpen(false);
      dispatch(getServiceRequest(serviceRequestId));
    } finally {
      setCompletionActionLoading(false);
    }
  };

  const handleSubmitReview = async (
    rating: number,
    title?: string,
    comment?: string
  ) => {
    setReviewError(undefined);
    let offerId = completion?.offerId || acceptedOffer?.offerId;
    if (!offerId) {
      try {
        const offersPage = await receivedOffersApi.getReceivedOffers(serviceRequestId, {
          pageNo: 1,
          pageSize: 50,
        });
        const completedOffer = offersPage.items?.find(
          (o: any) =>
            o.statusCode === OfferStatusCode.serviceCompleted ||
            o.statusCode === OfferStatusCode.workCompleted ||
            o.statusCode === OfferStatusCode.accepted ||
            o.statusCode === OfferStatusCode.confirmed ||
            o.statusCode === OfferStatusCode.workInProgress
        );
        offerId = completedOffer?.id;
      } catch {
        // Offers fetch failed
      }
    }
    if (!offerId) {
      setReviewError(translate("servicing:reviews.reviewSubmitError"));
      return;
    }
    try {
      await dispatch(
        createReview({ serviceRequestId, offerId, rating, title, comment })
      ).unwrap();
      setReviewDialogOpen(false);
      setReviewError(undefined);
      dispatch(fetchReviewsForServiceRequest(serviceRequestId));
    } catch {
      setReviewError(translate("servicing:reviews.reviewSubmitError"));
    }
  };

  // ===================================================================
  //  LOG-5: PDF Export
  // ===================================================================
  const handleExportPdf = useCallback(async () => {
    const el = logbookPdfRef.current;
    if (!el) return;
    setPdfExporting(true);
    try {
      const html2canvas = (await import("html2canvas")).default;
      const { jsPDF } = await import("jspdf");

      // Temporarily expand all for full snapshot
      const prevExpanded = new Set(expandedIds);
      const prevAllExpanded = allExpanded;
      setExpandedIds(new Set(entries.filter(e => e.kind === "milestone" && (e as LogbookMilestoneEntry).status !== "pending").map(e => e.id)));
      setAllExpanded(true);

      // Wait for DOM to update
      await new Promise(r => setTimeout(r, 350));

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#FFFFFF",
        logging: false,
      });

      // Restore expand state
      setExpandedIds(prevExpanded);
      setAllExpanded(prevAllExpanded);

      const imgData = canvas.toDataURL("image/png");
      const imgWidth = 190; // mm (A4 - margins)
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const pdf = new jsPDF("p", "mm", "a4");

      // Title
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.text("Service Logbook", 10, 15);
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      pdf.text(`Exported: ${formatDateTimeAt(new Date())}`, 10, 22);
      if (serviceRequestIssue) {
        pdf.text(`Issue: ${serviceRequestIssue.substring(0, 80)}`, 10, 28);
      }

      // Add image — handle multi-page if needed
      const startY = serviceRequestIssue ? 34 : 28;
      const pageHeight = 297; // A4 height in mm
      const availableHeight = pageHeight - startY - 10;

      if (imgHeight <= availableHeight) {
        pdf.addImage(imgData, "PNG", 10, startY, imgWidth, imgHeight);
      } else {
        // Multi-page: slice the canvas
        let yOffset = 0;
        let page = 0;
        const sliceHeight = Math.floor((availableHeight / imgHeight) * canvas.height);

        while (yOffset < canvas.height) {
          if (page > 0) {
            pdf.addPage();
          }
          const pageCanvas = document.createElement("canvas");
          pageCanvas.width = canvas.width;
          const remaining = canvas.height - yOffset;
          pageCanvas.height = Math.min(sliceHeight, remaining);
          const ctx = pageCanvas.getContext("2d");
          if (ctx) {
            ctx.drawImage(
              canvas,
              0, yOffset, canvas.width, pageCanvas.height,
              0, 0, canvas.width, pageCanvas.height
            );
          }
          const sliceData = pageCanvas.toDataURL("image/png");
          const sliceImgHeight = (pageCanvas.height * imgWidth) / canvas.width;
          pdf.addImage(sliceData, "PNG", 10, page === 0 ? startY : 10, imgWidth, sliceImgHeight);
          yOffset += pageCanvas.height;
          page++;
        }
      }

      pdf.save(`service-logbook-${dayjs().format("YYYY-MM-DD")}.pdf`);
    } catch (err) {
      console.error("PDF export failed:", err);
    } finally {
      setPdfExporting(false);
    }
  }, [entries, expandedIds, allExpanded, serviceRequestIssue]);

  // ===================================================================
  //  Milestone title lookup
  // ===================================================================
  const getMilestoneTitle = (type: MilestoneType): string => {
    const key = `servicing:rfsDetail.logbook.milestone.${type}`;
    return translate(key);
  };

  // ===================================================================
  //  Text truncation helper
  // ===================================================================
  const TruncatedText = ({ text, lines = 3 }: { text: string; lines?: number }) => {
    const [showFull, setShowFull] = useState(false);
    return (
      <Box>
        <Box
          sx={{
            fontSize: "13px",
            fontFamily: dt.font,
            fontWeight: 400,
            lineHeight: 1.6,
            color: "#374151",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            ...(!showFull && {
              display: "-webkit-box",
              WebkitLineClamp: lines,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }),
          }}
        >
          {text}
        </Box>
        {text.length > 200 && (
          <Box
            component="button"
            onClick={() => setShowFull(!showFull)}
            sx={{
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
              fontSize: "12px",
              fontWeight: 600,
              fontFamily: dt.font,
              color: dt.blue,
              mt: 0.5,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {showFull
              ? translate("servicing:rfsDetail.showLess")
              : translate("servicing:rfsDetail.showMore")}
          </Box>
        )}
      </Box>
    );
  };

  // ===================================================================
  //  Format helpers
  // ===================================================================
  const formatCurrency = (value?: number): string => {
    if (value === undefined || value === null) return "\u2014";
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // ===================================================================
  //  Render children content per milestone type
  // ===================================================================
  const renderMilestoneContent = (entry: LogbookMilestoneEntry): React.ReactNode => {
    switch (entry.type) {
      case "published":
        return renderPublishedContent(entry);
      case "offerAccepted":
        return renderOfferAcceptedContent(entry);
      case "serviceConfirmed":
        return renderServiceConfirmedContent(entry);
      case "workStarted":
        return renderWorkStartedContent(entry);
      case "workCompleted":
        return renderWorkCompletedContent(entry);
      case "ownerConfirmed":
        return null; // Timestamp in header is sufficient
      case "reviewLeft":
        return renderReviewContent(entry);
      default:
        return null;
    }
  };

  // --- Published: offer count badge + "View offers" ---
  const renderPublishedContent = (entry: LogbookMilestoneEntry) => {
    const { offersCount: count, newOffersCount: newCount } = entry.data;
    const hasOffers = count > 0;
    return (
      <Box
        sx={{
          backgroundColor: "#F0F7FF",
          border: "1px solid #BFDBFE",
          borderRadius: dt.radiusSm,
          p: isPhone ? 1.5 : 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Box
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              fontFamily: dt.font,
              color: "#1E293B",
            }}
          >
            {hasOffers
              ? translate("servicing:rfsDetail.offers.offersReceived", { count })
              : translate("servicing:rfsDetail.offers.noOffersYet")}
          </Box>
          {newCount > 0 && (
            <Box
              sx={{
                fontSize: "11px",
                fontWeight: 700,
                fontFamily: dt.font,
                color: "#FFF",
                backgroundColor: "#EF4444",
                borderRadius: "10px",
                padding: "1px 8px",
                lineHeight: "18px",
              }}
            >
              {newCount} {translate("servicing:newOffers")}
            </Box>
          )}
        </Box>
        {hasOffers && (
          <Box
            component="button"
            onClick={() => setOffersDialogOpen(true)}
            sx={{
              mt: 1.5,
              background: accentGradient,
              color: "#FFF",
              border: "none",
              borderRadius: dt.radiusPill,
              padding: "7px 18px",
              fontSize: "12px",
              fontWeight: 600,
              fontFamily: dt.font,
              cursor: "pointer",
              transition: "all 0.2s",
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              "&:hover": {
                background: accentGradientHover,
                transform: "translateY(-1px)",
                boxShadow: "0 4px 12px rgba(37, 99, 235, 0.25)",
              },
            }}
          >
            <LocalOfferOutlinedIcon sx={{ fontSize: "14px" }} />
            {translate("servicing:rfsDetail.offers.viewOffers")}
          </Box>
        )}
      </Box>
    );
  };

  // --- Offer Accepted: provider name + logo + price + message + attachments + view offer ---
  const renderOfferAcceptedContent = (entry: LogbookMilestoneEntry) => {
    const {
      providerName, providerLogoUrl, providerId,
      totalEstimatedCost, earliestStartDate, estimatedDuration,
      messageToCustomer, offerFiles,
    } = entry.data;
    return (
      <Box
        sx={{
          backgroundColor: "#FEF9E7",
          border: "1px solid #FDE68A",
          borderRadius: dt.radiusSm,
          p: isPhone ? 1.5 : 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {/* Provider logo + name (clickable) + price */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {providerLogoUrl ? (
            <Box
              component="img"
              src={providerLogoUrl}
              alt={providerName}
              sx={{ width: 36, height: 36, borderRadius: "8px", objectFit: "cover", flexShrink: 0 }}
            />
          ) : (
            <Box
              sx={{
                width: 36, height: 36, borderRadius: "8px",
                backgroundColor: "#FEF3C7",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}
            >
              <Box sx={{ fontSize: "14px", fontWeight: 700, fontFamily: dt.font, color: "#F59E0B" }}>
                {providerName?.charAt(0)?.toUpperCase() || "?"}
              </Box>
            </Box>
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ProviderNameLink name={providerName} providerId={providerId} />
            {totalEstimatedCost != null && (
              <Box sx={{ fontSize: "16px", fontWeight: 700, fontFamily: dt.fontMono, color: "#F59E0B" }}>
                {formatCurrency(totalEstimatedCost)}
              </Box>
            )}
          </Box>
        </Box>

        {/* Detail pills (earliest start, duration) */}
        {(earliestStartDate || estimatedDuration) && (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {earliestStartDate && (
              <DetailPill
                label={translate("serviceOffers:earliestStartDate")}
                value={dayjs(earliestStartDate).format("MMM D, YYYY")}
              />
            )}
            {estimatedDuration && (
              <DetailPill
                label={translate("serviceOffers:estimatedDuration")}
                value={estimatedDuration}
              />
            )}
          </Box>
        )}

        {/* Provider's comment/message */}
        {messageToCustomer && (
          <Box sx={{
            backgroundColor: "#FFF",
            borderRadius: "6px",
            borderLeft: "3px solid #F59E0B",
            px: 1.5, py: 1,
          }}>
            <Box sx={{ fontSize: "10px", fontWeight: 600, fontFamily: dt.font, textTransform: "uppercase", letterSpacing: "0.5px", color: "#6B7280", mb: 0.5 }}>
              {translate("servicing:rfsDetail.logbook.providerMessage")}
            </Box>
            <TruncatedText text={messageToCustomer} />
          </Box>
        )}

        {/* Offer attachments */}
        {offerFiles && offerFiles.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Box sx={{ fontSize: "10px", fontWeight: 600, fontFamily: dt.font, textTransform: "uppercase", letterSpacing: "0.5px", color: "#6B7280" }}>
              {translate("servicing:rfsDetail.logbook.attachments", { count: offerFiles.length })}
            </Box>
            {offerFiles.map((f: { name: string; size: number }, i: number) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: "12px", fontFamily: dt.font, color: "#374151" }}>
                <AttachFileOutlinedIcon sx={{ fontSize: 14, color: "#9CA3AF" }} />
                <Box sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</Box>
                {f.size > 0 && (
                  <Box sx={{ fontSize: "11px", color: "#9CA3AF", flexShrink: 0 }}>
                    ({(f.size / 1024).toFixed(0)} KB)
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}

        {/* "View Full Offer" button */}
        <Box
          component="button"
          onClick={() => setOffersDialogOpen(true)}
          sx={{
            background: "none",
            border: "none",
            padding: 0,
            cursor: "pointer",
            fontSize: "12px",
            fontWeight: 600,
            fontFamily: dt.font,
            color: dt.blue,
            mt: 0.5,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {translate("servicing:rfsDetail.logbook.viewFullOffer")}
          <OpenInNewIcon sx={{ fontSize: 13 }} />
        </Box>
      </Box>
    );
  };

  // --- Service Confirmed: provider logo + name + arrival date + time + message ---
  const renderServiceConfirmedContent = (entry: LogbookMilestoneEntry) => {
    const { providerName, providerLogoUrl, providerId, confirmedArrivalDate, confirmedArrivalTimeFrom, confirmedArrivalTimeTo, confirmationMessage } = entry.data;
    if (!confirmedArrivalDate && !confirmationMessage && !providerName) return null;
    return (
      <Box
        sx={{
          backgroundColor: "#ECFDF5",
          border: "1px solid #A7F3D0",
          borderRadius: dt.radiusSm,
          p: isPhone ? 1.5 : 2,
          display: "flex",
          flexDirection: "column",
          gap: 1,
        }}
      >
        {/* Provider logo + name */}
        {providerName && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            {providerLogoUrl ? (
              <Box
                component="img"
                src={providerLogoUrl}
                alt={providerName}
                sx={{ width: 32, height: 32, borderRadius: "6px", objectFit: "cover", flexShrink: 0 }}
              />
            ) : (
              <Box
                sx={{
                  width: 32, height: 32, borderRadius: "6px",
                  backgroundColor: "#D1FAE5",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}
              >
                <Box sx={{ fontSize: "13px", fontWeight: 700, fontFamily: dt.font, color: "#10B981" }}>
                  {providerName?.charAt(0)?.toUpperCase() || "?"}
                </Box>
              </Box>
            )}
            <ProviderNameLink name={providerName} providerId={providerId} />
          </Box>
        )}

        {/* Arrival date + time pills */}
        {confirmedArrivalDate && (
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <DetailPill
              label={translate("servicing:arrivalDate")}
              value={dayjs(confirmedArrivalDate).format("ddd, MMM D, YYYY")}
            />
            {confirmedArrivalTimeFrom && confirmedArrivalTimeTo && (
              <DetailPill
                label={translate("servicing:arrivalTime")}
                value={`${confirmedArrivalTimeFrom} - ${confirmedArrivalTimeTo}`}
              />
            )}
          </Box>
        )}

        {/* Confirmation message */}
        {confirmationMessage && (
          <Box
            sx={{
              backgroundColor: "#FFF",
              borderRadius: "6px",
              borderLeft: "3px solid #10B981",
              px: 1.5,
              py: 1,
            }}
          >
            <TruncatedText text={confirmationMessage} />
          </Box>
        )}
      </Box>
    );
  };

  // --- Work Started: provider name + timestamp ---
  const renderWorkStartedContent = (entry: LogbookMilestoneEntry) => {
    const { providerName, providerId, workStartedDate } = entry.data;
    if (!providerName) return null;
    const formattedDate = workStartedDate
      ? formatDateTimeAt(workStartedDate, true)
      : entry.timestamp
        ? formatDateTimeAt(entry.timestamp, true)
        : null;

    return (
      <Box sx={{
        backgroundColor: "#F0F9FF",
        border: "1px solid #BAE6FD",
        borderRadius: dt.radiusSm,
        px: isPhone ? 1.5 : 2,
        py: 1.25,
        fontSize: "13px",
        fontFamily: dt.font,
        color: "#374151",
        lineHeight: 1.6,
        display: "flex",
        alignItems: "baseline",
        flexWrap: "wrap",
        gap: 0.5,
      }}>
        <ProviderNameLink name={providerName} providerId={providerId} fontSize="13px" fontWeight={600} />
        {formattedDate && (
          <Box component="span" sx={{ color: "#6B7280" }}>
            {translate("servicing:rfsDetail.logbook.startedWorkAt")} {formattedDate}
          </Box>
        )}
      </Box>
    );
  };

  // --- Work Completed: completion card + action buttons ---
  const renderWorkCompletedContent = (entry: LogbookMilestoneEntry) => {
    const isAwaitingConfirmation =
      statusCode === ServiceRequestStatusCode.awaitingCompletionConfirmation;

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {completion && (
          <RfsCompletionCard
            completion={completion}
            serviceRequestId={serviceRequestId}
            files={acceptedOffer?.workCompletionFiles}
            embedded
          />
        )}
        {/* Confirm / Revise action buttons (only when awaiting confirmation) */}
        {isAwaitingConfirmation && completion && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: isPhone ? 1.5 : 2,
              flexDirection: isPhone ? "column" : "row",
            }}
          >
            <Button
              variant="contained"
              startIcon={<CheckCircleOutlinedIcon sx={{ fontSize: "18px !important" }} />}
              onClick={() => setConfirmDialogOpen(true)}
              sx={{
                background: "linear-gradient(135deg, #10B981, #059669)",
                borderRadius: dt.radiusPill,
                textTransform: "none",
                fontWeight: 700,
                fontFamily: dt.font,
                fontSize: "14px",
                px: 3.5,
                py: 1.25,
                boxShadow: "0 2px 8px rgba(16, 185, 129, 0.25)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  background: "linear-gradient(135deg, #059669, #10B981)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(16, 185, 129, 0.3)",
                },
              }}
            >
              {translate("servicing:completion.confirmCompletion")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<ReplayOutlinedIcon sx={{ fontSize: "18px !important" }} />}
              onClick={() => setRevisionDialogOpen(true)}
              sx={{
                borderColor: "#F59E0B",
                color: "#D97706",
                borderRadius: dt.radiusPill,
                textTransform: "none",
                fontWeight: 700,
                fontFamily: dt.font,
                fontSize: "14px",
                px: 3,
                py: 1.1,
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                "&:hover": {
                  borderColor: "#D97706",
                  backgroundColor: "rgba(245, 158, 11, 0.06)",
                  transform: "translateY(-2px)",
                  boxShadow: "0 4px 12px rgba(245, 158, 11, 0.15)",
                },
              }}
            >
              {translate("servicing:completion.requestRevision")}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  // --- Review Left: existing reviews or "Leave Review" prompt ---
  const renderReviewContent = (entry: LogbookMilestoneEntry) => {
    return (
      <RfsReviewSection
        reviews={srReviews}
        hasReviewed={hasReviewed}
        reviewDismissed={reviewDismissed}
        onLeaveReview={() => setReviewDialogOpen(true)}
        onDismissPrompt={() => setReviewDismissed(true)}
      />
    );
  };

  // ===================================================================
  //  Render progress note (minor entry)
  // ===================================================================
  const renderProgressNote = (note: LogbookProgressNote, isLast: boolean) => (
    <LogbookMilestone
      key={note.id}
      type="progressNote"
      title={note.authorName}
      timestamp={note.timestamp}
      status="completed"
      color="#0EA5E9"
      icon={<StickyNote2OutlinedIcon sx={{ fontSize: 14 }} />}
      expanded={expandedIds.has(note.id)}
      onToggle={() => toggleExpand(note.id)}
      isLast={isLast}
      isMinor
    >
      <Box
        sx={{
          backgroundColor: "#F0F7FF",
          border: `1px solid ${dt.cardBorder}`,
          borderLeft: "3px solid #0EA5E9",
          borderRadius: dt.radiusSm,
          px: isPhone ? 1.5 : 2,
          py: 1.25,
        }}
      >
        <TruncatedText text={note.message} />
      </Box>
    </LogbookMilestone>
  );

  // ===================================================================
  //  Don't render logbook if status is draft
  // ===================================================================
  if (statusCode === ServiceRequestStatusCode.draft) return null;

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0, position: "relative" }} ref={logbookContainerRef}>
        {/* Section header with Expand/Collapse toggle + PDF export */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <Box
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              fontFamily: dt.font,
              textTransform: "uppercase",
              letterSpacing: "1.2px",
              color: dt.textMuted,
            }}
          >
            {translate("servicing:rfsDetail.logbook.sectionTitle")}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {/* PDF Export button */}
            <Box
              component="button"
              onClick={handleExportPdf}
              disabled={pdfExporting}
              sx={{
                background: "none",
                border: "none",
                padding: "2px 8px",
                cursor: pdfExporting ? "wait" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: "11px",
                fontWeight: 600,
                fontFamily: dt.font,
                color: dt.textSecondary,
                borderRadius: dt.radiusSm,
                transition: "all 0.15s ease",
                opacity: pdfExporting ? 0.6 : 1,
                "&:hover": {
                  backgroundColor: dt.blueGlow,
                  color: dt.blue,
                },
              }}
            >
              {pdfExporting ? (
                <CircularProgress size={12} sx={{ color: dt.textSecondary }} />
              ) : (
                <PictureAsPdfOutlinedIcon sx={{ fontSize: 14 }} />
              )}
              {translate("servicing:rfsDetail.logbook.exportPdf")}
            </Box>
            {/* Expand/Collapse toggle */}
            {entries.length > 1 && (
              <Box
                component="button"
                onClick={toggleExpandAll}
                sx={{
                  background: "none",
                  border: "none",
                  padding: "2px 8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: "11px",
                  fontWeight: 600,
                  fontFamily: dt.font,
                  color: dt.textSecondary,
                  borderRadius: dt.radiusSm,
                  transition: "all 0.15s ease",
                  "&:hover": {
                    backgroundColor: dt.blueGlow,
                    color: dt.blue,
                  },
                }}
              >
                {allExpanded ? (
                  <>
                    <UnfoldLessIcon sx={{ fontSize: 14 }} />
                    {translate("servicing:rfsDetail.logbook.collapseAll")}
                  </>
                ) : (
                  <>
                    <UnfoldMoreIcon sx={{ fontSize: 14 }} />
                    {translate("servicing:rfsDetail.logbook.expandAll")}
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Timeline entries */}
        <Box sx={{ position: "relative" }} ref={logbookPdfRef}>
          {entries.map((entry, index) => {
            const isLast = index === entries.length - 1;

            if (entry.kind === "progressNote") {
              return renderProgressNote(entry as LogbookProgressNote, isLast);
            }

            const ms = entry as LogbookMilestoneEntry;
            const isCurrent = ms.status === "current";
            return (
              <LogbookMilestone
                key={ms.id}
                type={ms.type}
                title={getMilestoneTitle(ms.type)}
                timestamp={ms.timestamp}
                status={ms.status}
                color={milestoneColorMap[ms.type]}
                icon={milestoneIcons[ms.type]}
                expanded={expandedIds.has(ms.id)}
                onToggle={() => toggleExpand(ms.id)}
                isLast={isLast}
                nodeRef={isCurrent ? currentMilestoneRef : undefined}
              >
                {ms.status !== "pending" ? renderMilestoneContent(ms) : null}
              </LogbookMilestone>
            );
          })}
        </Box>

        {/* LOG-5: "Jump to latest" sticky chip — phone only */}
        {isPhone && showJumpChip && (
          <Box
            sx={{
              position: "sticky",
              bottom: 16,
              display: "flex",
              justifyContent: "center",
              zIndex: 10,
              pointerEvents: "none",
            }}
          >
            <Chip
              label={translate("servicing:rfsDetail.logbook.jumpToLatest")}
              icon={<KeyboardArrowDownIcon sx={{ fontSize: "18px !important" }} />}
              onClick={scrollToLatest}
              sx={{
                pointerEvents: "auto",
                fontFamily: dt.font,
                fontWeight: 700,
                fontSize: "12px",
                background: accentGradient,
                color: "#FFF",
                borderRadius: dt.radiusPill,
                boxShadow: "0 4px 16px rgba(37, 99, 235, 0.3)",
                transition: "all 0.2s ease",
                cursor: "pointer",
                "& .MuiChip-icon": { color: "#FFF" },
                "&:hover": {
                  background: accentGradientHover,
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(37, 99, 235, 0.4)",
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* ================================================================
          DIALOGS — rendered outside layout flow
          ================================================================ */}
      <CompareOffersDialog
        open={offersDialogOpen}
        serviceRequestId={serviceRequestId}
        serviceRequestIssue={serviceRequestIssue}
        onClose={() => setOffersDialogOpen(false)}
        onOfferAccepted={() => {
          setOffersDialogOpen(false);
          onOfferAction?.();
        }}
      />

      <ConfirmCompletionDialog
        open={confirmDialogOpen}
        completion={completion}
        files={acceptedOffer?.workCompletionFiles}
        onConfirm={handleConfirmCompletion}
        onRequestRevision={() => {
          setConfirmDialogOpen(false);
          setRevisionDialogOpen(true);
        }}
        onClose={() => setConfirmDialogOpen(false)}
        loading={completionActionLoading}
      />

      <RequestRevisionDialog
        open={revisionDialogOpen}
        onSubmit={handleRequestRevision}
        onClose={() => setRevisionDialogOpen(false)}
        loading={completionActionLoading}
      />

      <LeaveReviewDialog
        open={reviewDialogOpen}
        revieweeCompanyName={
          completion?.providerName || acceptedOffer?.providerName || "Service Provider"
        }
        onSubmit={handleSubmitReview}
        onSkip={() => {
          setReviewDialogOpen(false);
          setReviewDismissed(true);
        }}
        onClose={() => setReviewDialogOpen(false)}
        loading={reviewSubmitting}
        error={reviewError}
      />
    </>
  );
};

// ===================================================================
//  Small sub-components
// ===================================================================

const ProviderNameLink = ({
  name,
  providerId,
  fontSize = "14px",
  fontWeight = 700,
}: {
  name: string;
  providerId?: string;
  fontSize?: string;
  fontWeight?: number;
}) => {
  const navigate = useNavigate();
  if (!providerId) {
    return (
      <Box sx={{ fontSize, fontWeight, fontFamily: dt.font, color: "#1E293B" }}>
        {name}
      </Box>
    );
  }
  return (
    <Box
      component="a"
      onClick={(e: React.MouseEvent) => { e.stopPropagation(); navigate(`/companies/${providerId}`); }}
      sx={{
        fontSize,
        fontWeight,
        fontFamily: dt.font,
        color: dt.blue,
        cursor: "pointer",
        textDecoration: "none",
        transition: "color 0.15s ease",
        "&:hover": {
          textDecoration: "underline",
          color: "#1D4ED8",
        },
      }}
    >
      {name}
    </Box>
  );
};

const DetailPill = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
    <Box
      sx={{
        fontSize: "10px",
        fontWeight: 600,
        fontFamily: dt.font,
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        color: "#6B7280",
      }}
    >
      {label}
    </Box>
    <Box
      sx={{
        fontSize: "13px",
        fontWeight: 600,
        fontFamily: dt.font,
        color: "#1E293B",
      }}
    >
      {value}
    </Box>
  </Box>
);

export default RfsUnifiedLogbook;
